// src/App.js
import "./App.css";
import Navbar from "./components/Navbar";
import NavbarLogIn from "./components/NavbarLogIn";
import Footer from "./components/Footer";
import { Box, Typography, CircularProgress } from "@mui/material";

// Pages
import HomePage from "./pages/HomePage";
import Usuarios from "./pages/Usuarios";
import Programas from "./pages/Programas";
import Course from "./pages/Course";
import Register from "./pages/Register";
import Login from "./pages/Login";
import Documentos from "./pages/Documentos";
import Trayectorias from "./pages/Trayectorias";
import Progresos from "./pages/Progresos";
import Informes from "./pages/Informes";
import Trayectoria from "./pages/Trayectoria";
import Platform from "./pages/Platformv2";
import GestionCursos from "./pages/GestionCursos";
import Inscripciones from "./pages/Inscripciones";
import Avisos from "./pages/Avisos";
import Notificaciones from "./pages/Notificaciones";

// Components
import PlatformLayout from "./components/PlatformLayout";
import Calendario from "./components/Calendario";
import BloquesCursosComponent from "./components/BloquesCursosComponent";
import ActividadesAMS from "./components/ActividadesAMS";
import ActividadesPA from "./components/ActividadesPA";
import ActividadesRE from "./components/ActividadesRE";
import ActividadesID from "./components/ActividadesID";
import ActividadesCRM from "./components/ActividadesCRM";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";

import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "./firebase";
import { useEffect, useState } from "react";

import Conocenos from "./pages/Conocenos";
import RegistroDoble from "./components/RegistroDoble";
import MiPerfil from "./components/MiPerfil";
import AdminSidebarMenu from "./components/AdminSideMenu";

// paginas / componentes
import BlockTimeline from "./pages/BlockTimeline";
import rawProgramsData from "./components/datav2.json";

// =======================
// Helpers para programas
// =======================
const normalizePrograms = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.programs)) return data.programs;
  if (data.id) return [data];
  return [];
};

const ALL_PROGRAMS = normalizePrograms(rawProgramsData);

// =======================
// Guardian de rutas por rol
// =======================
function ProtectedRoute({ userInfo, allowedRoles, redirectTo = "/Inicio", children }) {
  const rolNombre = (userInfo?.rol?.nombre_rol || "").toLowerCase();

  if (!rolNombre) return <Navigate to={redirectTo} replace />;

  if (!allowedRoles.includes(rolNombre)) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}

function AppInner() {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [backendError, setBackendError] = useState(null);
  const [modOrAdmin, setModOrAdmin] = useState(false);
  const [needsDoubleRegister, setNeedsDoubleRegister] = useState(false);

  const location = useLocation();
  const path = location.pathname.toLowerCase();
  const hideNavbar = ["/plataforma", "/miperfil"].includes(path);

  useEffect(() => {
    let active = true;
    console.log("App montada: registrando onAuthStateChanged...");

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!active) return;
      setCheckingAuth(true);
      setBackendError(null);

      if (user) {
        console.log("Firebase user detectado:", {
          uid: user.uid,
          email: user.email,
        });
        setFirebaseUser(user);

        try {
          const token = await user.getIdToken(true);
          console.log("Validando en backend /public/validar-usuario ...");

          const res = await fetch(
            "https://vol-backend.onrender.com/public/validar-usuario",
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (res.status === 404) {
            console.warn("UID valido, NO existe en BD -> activar RegistroDoble");
            setUserInfo(null);
            setModOrAdmin(false);
            setNeedsDoubleRegister(true);
          } else if (res.ok) {
            const data = await res.json();
            console.log("Usuario en BD:", {
              id: data?.id,
              uid: data?.uid,
              rol: data?.rol?.nombre_rol || null,
            });
            setUserInfo(data);
            setNeedsDoubleRegister(false);

            const rolNombre = (data?.rol?.nombre_rol || "").toLowerCase();
            setModOrAdmin(["admin", "moderador"].includes(rolNombre));
          } else if (res.status === 401 || res.status === 403) {
            const errJson = await res.json().catch(() => null);
            const msg = errJson?.error || `Token invalido (${res.status})`;
            console.error("Auth backend rechazo token:", res.status, msg);
            setBackendError(msg);
            setNeedsDoubleRegister(false);
            await signOut(auth);
            setFirebaseUser(null);
            setUserInfo(null);
            setModOrAdmin(false);
          } else {
            const errJson = await res.json().catch(() => null);
            const msg = errJson?.error || `Error backend: ${res.status}`;
            console.error("Backend error no esperado:", res.status, msg);
            setBackendError(msg);
            setUserInfo(null);
            setModOrAdmin(false);
            setNeedsDoubleRegister(false);
          }
        } catch (error) {
          console.error("Error de red/otro al validar usuario:", error);
          setBackendError(error.message || "Error de red al validar usuario");
          setUserInfo(null);
          setModOrAdmin(false);
          setNeedsDoubleRegister(false);
        }
      } else {
        console.log("No hay sesion Firebase.");
        setFirebaseUser(null);
        setUserInfo(null);
        setModOrAdmin(false);
        setNeedsDoubleRegister(false);
      }

      if (active) setCheckingAuth(false);
    });

    return () => {
      active = false;
      unsubscribe();
      console.log("App desmontada: onAuthStateChanged desuscrito.");
    };
  }, []);

  if (checkingAuth) return <div className="loading">Cargando la app...</div>;

  if (backendError) {
    return (
      <div className="error">
        <p>Error validando usuario: {backendError}</p>
        <button onClick={() => signOut(auth)}>Cerrar sesion</button>
      </div>
    );
  }

  if (needsDoubleRegister) {
    const current = firebaseUser || auth.currentUser;

    console.log("Render: RegistroDoble (needsDoubleRegister=true)", {
      hasFirebaseUser: !!current,
      uid: current?.uid || null,
    });

    return (
      <div className="App">
        {!hideNavbar && <NavbarLogIn />}
        <RegistroDoble firebaseUser={current} />
        <Footer />
      </div>
    );
  }

  // =======================
  // Grupos de roles
  // =======================
  // Progresos, Usuarios, Documentos, Trayectorias -> moderadores estatales tambien acceden
  const STAFF_ROLES = ["admin", "moderador"];
  // Informes, Cursos, Inscripciones, Avisos -> SOLO sede nacional / super admin
  const ADMIN_ONLY = ["admin"];

  // Rol string del usuario actual (para pasarlo como prop)
  const rolActual = (userInfo?.rol?.nombre_rol || "moderador").toLowerCase();
  const uidActual = userInfo?.uid || null;

  return (
    <div className="App">
      {!hideNavbar && (userInfo ? <NavbarLogIn /> : <Navbar />)}

      {/* Se pasa userRole para que AdminSidebarMenu filtre su menu correctamente */}
      {modOrAdmin && <AdminSidebarMenu userRole={rolActual} />}

      <Routes>
        {userInfo ? (
          <>
            {/* Redireccion de /Inicio -> /Plataforma cuando hay sesion */}
            <Route path="/Inicio" element={<Navigate to="/Plataforma" replace />} />

            <Route
              path="/Plataforma"
              element={
                <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                  <Platform user={userInfo} />
                </PlatformLayout>
              }
            />

            <Route
              path="/MiPerfil"
              element={
                <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                  <MiPerfil user={userInfo} />
                </PlatformLayout>
              }
            />

            <Route
              path="/Programa"
              element={
                <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                  <ProgramaPage userRole={rolActual} />
                </PlatformLayout>
              }
            />

            {/* Trayectoria propia del aspirante - sin restriccion de rol */}
            <Route
              path="/Trayectoria"
              element={
                <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                  <Trayectoria />
                </PlatformLayout>
              }
            />

            {/* STAFF (admin + moderador) */}
            <Route
              path="/Usuarios"
              element={
                <ProtectedRoute userInfo={userInfo} allowedRoles={STAFF_ROLES} redirectTo="/Plataforma">
                  <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                    <Usuarios userRole={rolActual} />   {/* 👈 única línea que cambia */}
                  </PlatformLayout>
                </ProtectedRoute>
              }
            />

            {/* STAFF (admin + moderador) */}
            <Route
              path="/Documentos"
              element={
                <ProtectedRoute userInfo={userInfo} allowedRoles={STAFF_ROLES} redirectTo="/Plataforma">
                  <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                    <Documentos />
                  </PlatformLayout>
                </ProtectedRoute>
              }
            />

            {/* STAFF (admin + moderador) */}
            <Route
              path="/Progresos"
              element={
                <ProtectedRoute userInfo={userInfo} allowedRoles={STAFF_ROLES} redirectTo="/Plataforma">
                  <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                    <Progresos />
                  </PlatformLayout>
                </ProtectedRoute>
              }
            />

            {/* STAFF (admin + moderador) */}
            <Route
              path="/Trayectorias"
              element={
                <ProtectedRoute userInfo={userInfo} allowedRoles={STAFF_ROLES} redirectTo="/Plataforma">
                  <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                    <Trayectorias />
                  </PlatformLayout>
                </ProtectedRoute>
              }
            />

            {/* ADMIN ONLY - Sede Nacional / Super Admin */}
            <Route
              path="/Informes"
              element={
                <ProtectedRoute userInfo={userInfo} allowedRoles={ADMIN_ONLY} redirectTo="/Plataforma">
                  <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                    <Informes />
                  </PlatformLayout>
                </ProtectedRoute>
              }
            />

            {/* ADMIN ONLY - Sede Nacional / Super Admin */}
            <Route
              path="/Cursos"
              element={
                <ProtectedRoute userInfo={userInfo} allowedRoles={ADMIN_ONLY} redirectTo="/Plataforma">
                  <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                    <GestionCursos />
                  </PlatformLayout>
                </ProtectedRoute>
              }
            />

            {/* ADMIN ONLY - Sede Nacional / Super Admin */}
            <Route
              path="/Inscripciones"
              element={
                <ProtectedRoute userInfo={userInfo} allowedRoles={ADMIN_ONLY} redirectTo="/Plataforma">
                  <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                    <Inscripciones />
                  </PlatformLayout>
                </ProtectedRoute>
              }
            />

            {/* ADMIN ONLY - Sede Nacional / Super Admin */}
            <Route
              path="/Avisos"
              element={
                <ProtectedRoute userInfo={userInfo} allowedRoles={ADMIN_ONLY} redirectTo="/Plataforma">
                  <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                    <Avisos />
                  </PlatformLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/Calendario"
              element={
                <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                  <Calendario />
                </PlatformLayout>
              }
            />

            {/* Rutas de cursos y actividades */}
            <Route
              path="/cursos/:cursoNombre"
              element={
                <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                  <BloquesCursosComponent />
                </PlatformLayout>
              }
            />

            <Route
              path="/actividades/ams"
              element={
                <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                  <ActividadesAMS />
                </PlatformLayout>
              }
            />

            <Route
              path="/actividades/pa"
              element={
                <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                  <ActividadesPA />
                </PlatformLayout>
              }
            />

            <Route
              path="/actividades/re"
              element={
                <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                  <ActividadesRE />
                </PlatformLayout>
              }
            />

            <Route
              path="/actividades/id"
              element={
                <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                  <ActividadesID />
                </PlatformLayout>
              }
            />

            <Route
              path="/actividades/crm"
              element={
                <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                  <ActividadesCRM />
                </PlatformLayout>
              }
            />
            <Route
                path="/Notificaciones"
                element={
                  <PlatformLayout modOrAdmin={modOrAdmin} userRole={rolActual} uid={uidActual} userInfo={userInfo}>
                    <Notificaciones userRole={rolActual} />
                  </PlatformLayout>
                }
              />
            <Route path="*" element={<Navigate to="/Plataforma" replace />} />
          </>
        ) : (
          <>
            <Route path="/" element={<HomePage />} />
            <Route path="/Inicio" element={<HomePage />} />
            <Route path="/Programas" element={<Programas />} />
            <Route path="/Conocenos" element={<Conocenos />} />
            <Route path="/Registrate" element={<Register />} />
            <Route path="/Ingresa" element={<Login />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>

      <Footer />
    </div>
  );
}

// =======================
// Pagina de programa dinamico
// =======================
// ─── Roles que ven todo sin bloqueo ───────────────────────────────────────────
const ROLES_PRIVILEGIADOS_PROG = ["coordinador_estatal", "facilitador", "admin_nacional", "admin", "moderador"];

function ProgramaPage({ userRole = "aspirante" }) {
  const [searchParams] = useSearchParams();
  const programCode = (searchParams.get("code") || "").trim().toUpperCase();

  const esAspirante = !ROLES_PRIVILEGIADOS_PROG.includes(userRole);
  const [inscAbierta, setInscAbierta] = useState(null); // null = cargando

  useEffect(() => {
    if (!programCode || !esAspirante) {
      setInscAbierta(true); // privilegiados: siempre abierto, no consultar
      return;
    }
    const check = async () => {
      try {
        const token = await auth.currentUser?.getIdToken(true);

        // 1. Verificar si el programa está abierto
        const res = await fetch(
          "https://vol-backend.onrender.com/admin/catalog/programs/status",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) { setInscAbierta(true); return; }
        const data = await res.json();
        const prog = (Array.isArray(data) ? data : []).find(p => p.code === programCode);
        const abierta = prog ? prog.inscripcion_abierta !== 0 : true;

        if (abierta) { setInscAbierta(true); return; }

        // 2. Cerrada — verificar si el usuario tiene al menos 1 doc subido
        const statusRes = await fetch(
          `https://vol-backend.onrender.com/progreso/inscripcion-status?code=${programCode}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!statusRes.ok) { setInscAbierta(false); return; }
        const statusData = await statusRes.json();

        // Si tiene docs subidos → dejar pasar aunque esté cerrada
        setInscAbierta(statusData.tiene_docs_subidos === true);
      } catch {
        setInscAbierta(true); // falla silenciosa → no bloquear
      }
    };
    check();
  }, [programCode, esAspirante]);

  if (!programCode) {
    return (
      <div style={{ padding: "16px" }}>
        Selecciona un programa desde la sección "Tus Programas".
      </div>
    );
  }

  // Cargando estado de inscripción
  if (inscAbierta === null) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <CircularProgress sx={{ color: "#c62828" }} />
      </Box>
    );
  }

  // Bloqueado — aspirante + inscripcion cerrada
  if (esAspirante && inscAbierta === false) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: "60vh", px: 2 }}>
        <Box sx={{
          backgroundColor: "#fff",
          border: "1px solid #ffcdd2",
          borderRadius: 3,
          p: { xs: 3, md: 5 },
          maxWidth: 520,
          textAlign: "center",
          boxShadow: "0 4px 20px rgba(198,40,40,0.08)",
        }}>
          <Typography sx={{ fontSize: "2.5rem", mb: 1 }}>🔒</Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#c62828", mb: 1 }}>
            Inscripciones cerradas
          </Typography>
          <Typography sx={{ color: "#555", fontSize: "0.9rem", lineHeight: 1.6 }}>
            Las inscripciones a este programa están cerradas temporalmente.
            Si tienes dudas, comunícate con tu coordinación local.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Acceso normal
  return (
    <BlockTimeline
      programCode={programCode}
      onActivityClick={(payload) => {
        console.log("Actividad clickeada:", payload);
      }}
    />
  );
}

function App() {
  return (
    <Router>
      <AppInner />
    </Router>
  );
}

export default App;